import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, 
  Alert, Container, Paper, Tabs, Tab, Divider, Link, Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  LockReset as LockResetIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { InputAdornment, IconButton } from '@mui/material';
import api from '../api';
import { tenantPasswordError } from '../utils/tenantPassword';
import { saveTenantSession, tenantDisplayName } from '../utils/tenantSession';

const TenantAuth = ({ onLogin, onRegister, onForgotPassword }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Registration form state
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!loginData.email || !loginData.password) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      const response = await api.post('/api/tenant-invitations/login', {
        email: loginData.email,
        password: loginData.password
      });
      saveTenantSession(response.data);
      console.log('✅ Tenant login successful');

      onLogin({
        id: response.data.tenantId,
        email: response.data.email,
        name: tenantDisplayName(response.data)
      });
      
    } catch (err) {
      console.error('❌ Login failed:', err);
      const errorMessage = err.response?.data?.error || 'Login failed. Please check your credentials.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validation
      if (registerData.password !== registerData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      const passwordError = tenantPasswordError(registerData.password);
      if (passwordError) {
        setError(passwordError);
        return;
      }

      // Mock registration - replace with real API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockTenant = {
        id: `tenant-${Date.now()}`,
        email: registerData.email,
        name: `${registerData.firstName} ${registerData.lastName}`,
        phone: registerData.phone,
        property: 'Pending Assignment',
        unit: 'Pending'
      };
      
      onRegister(mockTenant);
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordClick = () => {
    setShowForgotPassword(true);
    setError('');
    setForgotPasswordSuccess(false);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail || !forgotPasswordEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/api/tenant-invitations/forgot-password', { email: forgotPasswordEmail });
      setForgotPasswordSuccess(true);
      if (onForgotPassword) onForgotPassword();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordSuccess(false);
    setError('');
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <PersonIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Tenant Portal
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Access your rental information and manage your account
            </Typography>
          </Box>

          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
            <Tab label="Login" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {forgotPasswordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              If an account exists with that email, you will receive a password reset link shortly. Check your inbox.
            </Alert>
          )}

          {showForgotPassword ? (
            <form onSubmit={handleForgotPasswordSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                <LockResetIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h5" gutterBottom>
                  Forgot Your Password?
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                  Enter your email address. We&apos;ll send you a link to set a new password.
                </Typography>
                <Chip label="Step 1 of 2" size="small" color="primary" variant="outlined" sx={{ mb: 1 }} />
              </Box>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <Button fullWidth variant="text" onClick={handleBackToLogin} sx={{ mt: 2 }}>
                Back to Login
              </Button>
            </form>
          ) : activeTab === 0 && (
            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleForgotPasswordClick}
                  sx={{ cursor: 'pointer' }}
                >
                  Forgot your password?
                </Link>
              </Box>
            </form>
          )}

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              New Tenant?
            </Typography>
          </Divider>

          <Box sx={{ textAlign: 'center' }}>
            <Alert severity="info" sx={{ textAlign: 'left' }}>
              <Typography variant="body2" gutterBottom>
                <strong>Don't have an account yet?</strong>
              </Typography>
              <Typography variant="body2">
                Your property manager will send you an invitation email with a link to set up your account. 
                Please check your inbox for the invitation.
              </Typography>
            </Alert>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default TenantAuth; 